import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CleanerCard from "./components/CleanerCard";
import CleanerProfileDrawer from "./components/CleanerProfileDrawer";
import {
  availabilityFilters,
  cityFilters,
  directoryCleaners,
  sortOptions,
  specialtyFilters,
  type DirectoryCleaner,
} from "@/mocks/cleanersDirectory";

const PAGE_SIZE = 12;

export default function CleanersPage() {
  const [query, setQuery] = useState("");
  const [specialty, setSpecialty] = useState<string>("all");
  const [city, setCity] = useState<string>("All cities");
  const [availability, setAvailability] = useState<string>("all");
  const [sort, setSort] = useState<string>("recommended");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState<DirectoryCleaner | null>(null);

  const filtered = useMemo(() => {
    let list = [...directoryCleaners];

    if (specialty !== "all") {
      list = list.filter((c) => c.specialties.includes(specialty));
    }
    if (city !== "All cities") {
      list = list.filter((c) => c.city === city);
    }
    if (availability !== "all") {
      list = list.filter((c) => c.availability === availability);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.role.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          c.languages.some((l) => l.toLowerCase().includes(q)),
      );
    }

    switch (sort) {
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      case "price-low":
        list.sort((a, b) => a.rate - b.rate);
        break;
      case "price-high":
        list.sort((a, b) => b.rate - a.rate);
        break;
      case "jobs":
        list.sort((a, b) => b.jobs - a.jobs);
        break;
      default:
        list.sort((a, b) => {
          const score = (c: DirectoryCleaner) =>
            c.rating * 100 + (c.availability === "today" ? 20 : c.availability === "this-week" ? 10 : 0);
          return score(b) - score(a);
        });
    }

    return list;
  }, [query, specialty, city, availability, sort]);

  const visibleList = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setQuery("");
    setSpecialty("all");
    setCity("All cities");
    setAvailability("all");
    setSort("recommended");
    setVisible(PAGE_SIZE);
  };

  return (
    <main className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 px-4 md:px-10 bg-background-100 border-b border-background-200/70">
        <div className="max-w-5xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-foreground-600 hover:text-primary-700 mb-5"
          >
            <i className="ri-arrow-left-line"></i>
            Back to home
          </Link>
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-foreground-600 mb-4">
            <span className="w-6 h-px bg-foreground-400"></span>
            All 320+ cleaners
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-medium text-foreground-950 leading-[1.05] max-w-3xl">
            Meet every PureNest pro.
          </h1>
          <p className="text-foreground-700 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
            Every cleaner is background-checked, in-person assessed and fairly paid.
            Browse profiles, filter by specialty, and book the one who feels right.
          </p>

          <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-8 text-sm text-foreground-800">
            <span className="inline-flex items-center gap-2">
              <i className="ri-shield-check-line text-primary-600 text-lg"></i>
              Background-checked
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-graduation-cap-line text-primary-600 text-lg"></i>
              In-person assessed
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-bank-card-line text-primary-600 text-lg"></i>
              $2M insured
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-leaf-line text-primary-600 text-lg"></i>
              Fair-pay certified
            </span>
          </div>
        </div>
      </section>

      {/* Search + filter bar */}
      <section className="sticky top-[72px] z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 px-4 md:px-10 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 max-w-2xl">
            <i className="ri-search-line text-foreground-500 text-sm absolute left-4 top-1/2 -translate-y-1/2"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              placeholder="Search by name, role, language or city"
              className="w-full bg-background-100 border border-background-200 rounded-full pl-11 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:bg-background-50"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SelectPill
              value={city}
              onChange={(v) => {
                setCity(v);
                setVisible(PAGE_SIZE);
              }}
              icon="ri-map-pin-line"
              options={cityFilters.map((c) => ({ value: c, label: c }))}
            />
            <SelectPill
              value={availability}
              onChange={(v) => {
                setAvailability(v);
                setVisible(PAGE_SIZE);
              }}
              icon="ri-time-line"
              options={availabilityFilters.map((a) => ({ value: a.id, label: a.label }))}
            />
            <SelectPill
              value={sort}
              onChange={(v) => {
                setSort(v);
              }}
              icon="ri-arrow-up-down-line"
              options={sortOptions.map((s) => ({ value: s.id, label: s.label }))}
            />
          </div>
        </div>

        {/* Specialty chips */}
        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
          {specialtyFilters.map((s) => {
            const active = specialty === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSpecialty(s.id);
                  setVisible(PAGE_SIZE);
                }}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-800 hover:bg-background-200"
                }`}
              >
                <i className={s.icon}></i>
                {s.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Results */}
      <section className="px-4 md:px-10 py-10 md:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground-950">
              {filtered.length} cleaner{filtered.length === 1 ? "" : "s"} match
            </h2>
            <p className="text-foreground-600 text-sm mt-1">
              Showing {visibleList.length} of {filtered.length} · sorted by{" "}
              {sortOptions.find((s) => s.id === sort)?.label.toLowerCase()}
            </p>
          </div>
          {(query || specialty !== "all" || city !== "All cities" || availability !== "all") && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-700 whitespace-nowrap self-start sm:self-auto"
            >
              <i className="ri-close-circle-line"></i>
              Clear filters
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-background-300 rounded-2xl">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
            </span>
            <p className="font-heading text-xl text-foreground-950 mt-4">No cleaners match those filters.</p>
            <p className="text-sm text-foreground-600 mt-2">
              Try clearing a filter or searching for a different city.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap"
            >
              Reset filters
              <i className="ri-refresh-line"></i>
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-7">
              {visibleList.map((c) => (
                <CleanerCard key={c.id} cleaner={c} onView={setSelected} />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-12">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 bg-foreground-950 hover:bg-primary-600 text-background-50 px-7 py-3 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
                >
                  Load more cleaners
                  <i className="ri-arrow-down-line"></i>
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="px-4 md:px-10 pb-20">
        <div className="rounded-3xl bg-primary-900 text-background-50 p-8 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-medium">
              Can't decide? We'll match you in 60 seconds.
            </h2>
            <p className="text-background-200/85 text-sm md:text-base mt-3 leading-relaxed">
              Skip the scroll. Tell us about your home and we'll auto-match you
              with the top-rated cleaner available for your slot.
            </p>
          </div>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 bg-background-50 text-primary-900 hover:bg-background-100 pl-6 pr-2 py-2.5 rounded-full text-sm font-medium whitespace-nowrap self-start md:self-auto transition-colors"
          >
            Auto-match me
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-magic-line"></i>
            </span>
          </Link>
        </div>
      </section>

      <Footer />

      <CleanerProfileDrawer cleaner={selected} onClose={() => setSelected(null)} />
    </main>
  );
}

function SelectPill({
  value,
  onChange,
  icon,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  icon: string;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="relative">
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-600 pointer-events-none">
        <i className={icon}></i>
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-background-100 border border-background-200 rounded-full pl-10 pr-9 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer hover:bg-background-200"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-600 pointer-events-none">
        <i className="ri-arrow-down-s-line"></i>
      </span>
    </div>
  );
}